import * as React from 'react'
import type { Map as LeafletMap, TileLayer } from 'leaflet'
import type { PostcodeLocation } from './postcodeLocation'
import { Button } from './ui'

// Includes Northern Ireland, the Isles of Scilly and Shetland.
const ukBounds: [[number, number], [number, number]] = [[49.7, -8.7], [61, 2.1]]
const tileUrl = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'

export function PostcodeMap({ location }: { location: PostcodeLocation }) {
  const container = React.useRef<HTMLDivElement>(null)
  const mapRef = React.useRef<LeafletMap | null>(null)
  const tilesRef = React.useRef<TileLayer | null>(null)
  const showingUK = React.useRef(true)
  const [ready, setReady] = React.useState(false)
  const [loadError, setLoadError] = React.useState(false)
  const [tileError, setTileError] = React.useState(false)
  const [revision, retryLoad] = React.useReducer(value => value + 1, 0)

  React.useEffect(() => {
    let cancelled = false
    let map: LeafletMap | undefined
    let resize: ResizeObserver | undefined
    setReady(false); setLoadError(false); setTileError(false)
    // The map library and its stylesheet are fetched only after opening a map.
    void Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')]).then(([L]) => {
      if (cancelled || !container.current) return
      map = L.map(container.current, { minZoom: 3, maxZoom: 18, zoomSnap: 0.5, zoomAnimation: false, fadeAnimation: false, markerZoomAnimation: false })
      mapRef.current = map
      map.fitBounds(ukBounds, { padding: [24, 36], animate: false })
      const tiles = L.tileLayer(tileUrl, {
        maxZoom: 19, noWrap: true,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>',
      }).addTo(map)
      tilesRef.current = tiles
      tiles.on('tileerror', () => { if (!cancelled) setTileError(true) })
      const icon = L.divIcon({ className: 'postcode-map-pin', html: '<span class="postcode-map-pin-shape"></span>', iconSize: [30, 40], iconAnchor: [15, 40] })
      const marker = L.marker([location.latitude, location.longitude], { icon, keyboard: false, title: location.postcode }).addTo(map)
      marker.getElement()?.setAttribute('role', 'img')
      marker.getElement()?.setAttribute('aria-label', `Location of ${location.postcode}`)
      const label = document.createElement('span')
      label.textContent = location.postcode
      marker.bindTooltip(label, { permanent: true, direction: 'top', offset: [0, -40], className: 'postcode-map-label' })
      map.on('dragstart zoomstart', () => { showingUK.current = false })
      map.fitBounds(ukBounds, { padding: [24, 36], animate: false })
      showingUK.current = true
      resize = new ResizeObserver(() => {
        if (!map || cancelled) return
        map.invalidateSize({ pan: false, animate: false })
        if (showingUK.current) {
          map.fitBounds(ukBounds, { padding: [24, 36], animate: false })
          showingUK.current = true
        }
      })
      resize.observe(container.current)
      setReady(true)
    }).catch(() => {
      if (cancelled) return
      resize?.disconnect(); map?.remove(); map = undefined; mapRef.current = null; tilesRef.current = null
      setLoadError(true)
    })
    return () => {
      cancelled = true
      resize?.disconnect(); map?.remove(); mapRef.current = null; tilesRef.current = null
    }
  }, [location, revision])

  return <div className="postcode-map-content">
    <div className="postcode-map-controls" aria-label="Map controls">
      <Button type="button" className="secondary" disabled={!ready} onClick={() => {
        showingUK.current = false
        mapRef.current?.setView([location.latitude, location.longitude], 12, { animate: false })
      }}>Zoom to postcode</Button>
      <Button type="button" className="secondary" disabled={!ready} onClick={() => {
        mapRef.current?.fitBounds(ukBounds, { padding: [24, 36], animate: false })
        showingUK.current = true
      }}>Show UK</Button>
    </div>
    {loadError ? <div className="postcode-map-status" role="alert"><p>The map could not load.</p><Button type="button" className="secondary" onClick={retryLoad}>Retry map</Button></div>
      : !ready && <p role="status">Loading map…</p>}
    {tileError && <div className="postcode-map-tile-error" role="alert"><span>Some map tiles could not load.</span><Button type="button" onClick={() => { setTileError(false); tilesRef.current?.redraw() }}>Retry map</Button></div>}
    <div ref={container} className="postcode-map-canvas" role="region" aria-label={`Map showing ${location.postcode}`} aria-busy={!ready} />
    <p className="postcode-map-hint">Approximate postcode location. Drag to pan and use the zoom controls or pinch to zoom.</p>
  </div>
}
